import { IVeriMapComponent } from "@app/directives/_map/veriMap/veriMapComponent/veriMap.component";
import { IGoogleMapsMarker } from "@app/interfaces/IGoogleMapsMarkerOptions";
import { IJourney } from "@app/interfaces/IJourney";
import { IPlot } from "@app/interfaces/IPlot";
import { ColourService } from "@app/services/colour.service";
import { DateService, DateUnit } from "@app/services/date.service";
import { Debounce } from "@app/services/debounce.service";
import { GoogleMarkerService } from "@app/services/googleMarker.service";
import { JourneyService } from "@app/services/journey.service";
import { MapService } from "@app/services/map.service";
import { PlotService } from "@app/services/plot.service";
import { TranslationService } from "@app/services/translation.service";
import { VeriDate } from "@app/services/veriDate.service";
import { CameraDisplayService } from "@components/videos/cameraDisplay.service";
import { IVehicle } from "@interfaces/IVehicle";
import { IPolyline } from "@typings/google.maps.angular";
import * as angular from "angular";
import { copy } from "angular";
import { IVideoEntry } from "../../models/IVideoEntry";
import { IVideoExtensionData } from "../../models/IVideoExtensionData";
import { VideoEntryService } from "../../services/video-entry.service";
import "./requestVideoMap.component.scss";


interface IComponentBindings {
    vehicle: IVehicle;
    video: IVideoEntry;
    requestVideoTime: Date;
    videoLength: number;
    isLiveStreamCamera: boolean;
    isOnline: boolean;

    onClosestPlotFound: (args: { plot: IPlot }) => void;
}

class ComponentController implements angular.IComponentController, IComponentBindings {

    // bindings
    vehicle: IVehicle;
    video: IVideoEntry;
    requestVideoTime: Date;
    videoLength: number;
    isLiveStreamCamera: boolean;
    isOnline: boolean;
    onClosestPlotFound: (args: { plot: IPlot }) => void;
    onJourneysLoaded: (args: { journeys: IJourney[] }) => void;


    veriMap: IVeriMapComponent;
    journeys: IJourney[];
    videoJourneys: IJourney[];
    highlightedJourney: IJourney;
    plotsForRequestDate: IPlot[];
    startMapMarker: IGoogleMapsMarker;
    endMapMarker: IGoogleMapsMarker;
    videoMarker: IGoogleMapsMarker;
    vehiclePolyline: IPolyline;

    mapLatLngs: google.maps.LatLng[]

    // Loading
    loading: boolean;

    private debounceRenderMap: () => void;

    static $inject = ["$scope", "$q", "cameraDisplayService", "colourService", "dateService", "debounceService", "googleMarkerService", "journeyService", "mapService", "plotService", "translationService", "veriDate", "videoEntryService"];

    constructor(
        private readonly $scope: angular.IScope,
        private readonly $q: angular.IQService,
        private readonly cameraDisplayService: CameraDisplayService,
        private readonly colourService: ColourService,
        private readonly dateService: DateService,
        private readonly debounceService: Debounce,
        private readonly googleMarkerService: GoogleMarkerService,
        private readonly journeyService: JourneyService,
        private readonly mapService: MapService,
        private readonly plotService: PlotService,
        private readonly translationService: TranslationService,
        private readonly veriDate: VeriDate,
        private readonly videoEntryService: VideoEntryService
    ) {
        this.debounceRenderMap = this.debounceService.debounce(() => { this.renderMap() }, 500, false);
    }

    $onChanges(changes: angular.IOnChangesObject): void {
        if (changes['requestVideoTime'] && this.requestTimeHasChanged(changes['requestVideoTime']) || changes['vehicle']) {
            this.debounceRenderMap();
        }

        this.$scope.$applyAsync();
    }

    onVeriMapLoaded(veriMap: IVeriMapComponent): void {
        this.veriMap = veriMap;
    }

    //--

    private buildJourneyMap(showLastKnownLocation: boolean = false): void {
        let promise = this.$q.resolve();

        if (showLastKnownLocation) {
            this.createLastKnownLocationMarker(this.vehicle.Plot);

        }
        else if (this.video) {
            const servicePromise = this.videoEntryService.getExtensionData(this.video) as angular.IPromise<IVideoExtensionData>;

            promise = servicePromise.then(extensionData => {
                if (extensionData?.VideoGpsData.length) {
                    return this.buildPolylineFromExtensionData();
                }
                else {
                    return this.createJourneysFromVideoData() as angular.IPromise<void>;
                }
            });
        }
        else {
            promise = this.createJourneysFromPlotData() as angular.IPromise<void>;
        }

        promise.then(() => {
            if (!this.veriMap) {
                console.warn("No map found for request video");
                return;
            }

            this.mapLatLngs = [];

            if (this.startMapMarker && this.endMapMarker) {
                this.mapLatLngs.push(new google.maps.LatLng(this.startMapMarker.options.position));
                this.mapLatLngs.push(new google.maps.LatLng(this.endMapMarker.options.position));
            }

            if (this.videoMarker) {
                this.mapLatLngs.push(new google.maps.LatLng(this.videoMarker.options.position));
            }

            this.fitMap();
        });
    }

    private fitMap() {
        if (this.vehiclePolyline?.path) {
            this.veriMap.fitMap(this.vehiclePolyline.path);
        }
        else {
            this.veriMap.fitMap(this.mapLatLngs);
        }
    }

    private getPlots(): angular.IPromise<void> {
        if (!this.requestVideoTime) {
            return this.$q.reject("requestVideoTime not found");
        }

        return this.plotService.getPlotsForDateRange(this.vehicle.Id,
            this.dateService.getStartOfDay(this.requestVideoTime),
            this.dateService.getEndOfDay(this.requestVideoTime))
            .then(plots => {
                this.plotsForRequestDate = plots;
            });
    }

    private createJourneysFromPlotData(): Promise<void> {
        return this.getJourneysPromise().then(() => {
            this.videoJourneys = [];
            const startTime = this.requestVideoTime;
            const endTime = this.dateService.addOrSubtractTime(this.requestVideoTime, this.videoLength, DateUnit.Sec);

            //Find the full journey(s) at the time of request
            const journeysAtTimeOfVideo: IJourney[] = [];
            this.journeys.forEach(journey => {
                if (this.dateService.dateIsBetweenDates(journey.StartUtc, startTime, endTime) ||
                    this.dateService.dateIsBetweenDates(journey.EndUtc, startTime, endTime) ||
                    this.dateService.dateIsBetweenDates(startTime, journey.StartUtc, journey.EndUtc) ||
                    this.dateService.dateIsBetweenDates(endTime, journey.StartUtc, journey.EndUtc) ||
                    (this.dateService.date1IsAfterDate2(journey.StartUtc, startTime) && !journey.EndUtc)) {
                    journeysAtTimeOfVideo.push(journey);
                }

            })

            if (!journeysAtTimeOfVideo.length) {
                const plotAtTime = this.plotService.findPlotClosestToTime(this.veriDate.createMoment(this.requestVideoTime), this.plotsForRequestDate);

                if (this.onClosestPlotFound) {
                    this.onClosestPlotFound({ plot: plotAtTime })
                }

                this.createApproximateLocationMarker(plotAtTime);
                return;
            }

            this.createVideoJourneys(journeysAtTimeOfVideo, startTime, endTime);
        }) as Promise<void>;
    }

    private createJourneysFromVideoData(): Promise<void> {
        if (!this.video) {
            return Promise.reject();
        }

        this.createVideoMarker();

        return this.journeyService.getJourneysForDateAndVehicle(this.video.VehicleId, new Date(this.video.EventTimeUtc)).then(journeys => {
            //Find the full journey at the time of the video
            const relevantJourneys: IJourney[] = journeys.filter(journey => {
                return this.veriDate.createMoment(journey.StartUtc).isBefore(this.veriDate.createMoment(this.video.VideoStartTime)) && (this.veriDate.createMoment(journey.EndUtc).isAfter(this.veriDate.createMoment(this.video.VideoEndTime)) || !journey.EndUtc)
            });

            if (!relevantJourneys.length) {
                return;
            }

            this.createVideoJourneys(relevantJourneys, this.video.VideoStartTime, this.video.VideoEndTime);
        }) as Promise<void>;
    }

    private createVideoJourneys(journeys: IJourney[], startTime: Date, endTime: Date): void {
        this.videoJourneys = [];
        this.clearMapMarkers();

        if (!journeys.some(journey => journey.LinePoints?.length)) {
            this.createApproximateLocationMarker({ Latitude: journeys[0].StartLatitude, Longitude: journeys[0].StartLongitude });
            return;
        }

        const allLinePoints = journeys.flatMap(journey => journey.LinePoints);

        const plotsFromLinePoints: IPlot[] = allLinePoints?.map(lp => {
            if (!lp) {
                return;
            }
            return {
                Latitude: lp[0], Longitude: lp[1]
            }
        })


        const startPlot = this.plotService.findPlotClosestToTime(this.veriDate.createMoment(startTime), this.plotsForRequestDate);
        const startPlotLatLng: google.maps.LatLng = new google.maps.LatLng({ lat: startPlot.Latitude, lng: startPlot.Longitude });

        const endPlot = this.plotService.findPlotClosestToTime(this.veriDate.createMoment(endTime), this.plotsForRequestDate);
        const endPlotLatLng: google.maps.LatLng = new google.maps.LatLng({ lat: endPlot.Latitude, lng: endPlot.Longitude });

        // Get the line points relevant to the requested timeframe
        const startLinePointPlot = this.plotService.findPlotClosestToLatLng(plotsFromLinePoints, startPlotLatLng);
        const endLinePointPlot = this.plotService.findPlotClosestToLatLng(plotsFromLinePoints, endPlotLatLng);


        let startLinePointIndex = allLinePoints.findIndex(lp => lp[0] == startLinePointPlot.Latitude && lp[1] == startLinePointPlot.Longitude);
        startLinePointIndex = startLinePointIndex > 0 ? startLinePointIndex - 1 : startLinePointIndex;

        let endLinePointIndex = allLinePoints.findIndex(lp => lp[0] == endLinePointPlot.Latitude && lp[1] == endLinePointPlot.Longitude);
        endLinePointIndex = endLinePointIndex < allLinePoints.length - 1 ? endLinePointIndex + 1 : endLinePointIndex;

        if (journeys.length) {
            journeys.forEach((journey, index) => {
                this.videoJourneys.push({
                    ...copy(journey),
                    lineColor: this.colourService.getColour(index + 1, true).solidColour
                });
            })

        }

        if (startPlot.Latitude === endPlot.Latitude &&
            startPlot.Longitude === endPlot.Longitude ||
            !journeys.length) {
            this.createApproximateLocationMarker(startPlot);
            return;
        } else {
            this.createStartMarker(plotsFromLinePoints[startLinePointIndex]);
            this.createEndMarker(plotsFromLinePoints[endLinePointIndex]);
        }

        // Create a separate journey to highlight the section of the requested/event time
        this.highlightedJourney = {

            ...copy(journeys[0]),
            Id: 0,
            LinePoints: allLinePoints.slice(startLinePointIndex, endLinePointIndex + 1),
            lineColor: this.colourService.getColour(0, true).solidColour
        }

        this.videoJourneys.push(this.highlightedJourney);

        this.videoJourneys.sort((a, b) => b.Id - a.Id);
    }

    private getJourneysPromise(): angular.IPromise<void> {
        if (this.journeys?.length) {
            return this.$q.resolve();
        }

        this.loading = true;

        const minDate = this.dateService.addOrSubtractTime(new Date, -1, DateUnit.Week);
        const maxDate = this.dateService.getEndOfDay(new Date);

        this.journeys = [];

        const promises = [];

        promises.push(this.journeyService.getJourneysForVehicle(this.vehicle.Id, minDate, maxDate)
            .then((journeys) => {
                if (!journeys?.length) {
                    return;
                }

                this.journeys.push(...journeys);
            })
            .catch((reason) => {
                console.error("Couldn't get journeys, reason: ", { reason });
            }))

        promises.push(this.journeyService.getCurrentJourney(this.vehicle.Id, true)
            .then((currentJourney) => {
                if (!currentJourney) {
                    return;
                }

                this.journeys.push(currentJourney);
            })
            .catch((reason) => {
                console.log("Couldn't get current journey, reason: ", { reason });
            }))

        return this.$q.all(promises).then(() => {
            this.loading = false;
            if (this.onJourneysLoaded) {
                this.onJourneysLoaded({ journeys: this.journeys })
            }
        });
    }

    private createApproximateLocationMarker(plot: IPlot) {
        const friendlyTime = this.dateService.formatDate(this.requestVideoTime, { timeOnly: true });

        this.videoMarker = this.mapService.createMapMarker(plot, { label: this.translationService.t("requestVideo.ApproximateLocationMarkerLabel", friendlyTime), subIcon: "videocam", showLabel: true });

    }

    private createVideoMarker() {
        if (this.video.Latitude === 0 && this.video.Longitude === 0) {
            this.videoMarker = null;
            return;
        }

        this.videoMarker = {
            id: 1,
            options: this.googleMarkerService.createEventMarkerOptions(
                {
                    label: this.translationService.t("requestVideo.eventLocation"),
                    subIcon: "videocam",
                    position: {
                        lat: this.video.Latitude,
                        lng: this.video.Longitude,
                    },
                    showLabel: true
                })
        };
    }

    private clearMapMarkers(): void {
        this.startMapMarker = null;
        this.endMapMarker = null;
        this.videoMarker = null;
    }

    private createStartMarker(plot: IPlot): void {
        this.startMapMarker = this.mapService.createMapMarker(plot, { label: this.translationService.t("requestVideo.start"), subIcon: "play_arrow", showLabel: true });
    }

    private createEndMarker(plot: IPlot): void {
        this.endMapMarker = this.mapService.createMapMarker(plot, { label: this.translationService.t("requestVideo.end"), subIcon: "flag", showLabel: true });
    }

    private createLastKnownLocationMarker(plot: IPlot) {
        this.videoMarker = this.mapService.createMapMarker(plot, { label: this.translationService.t("requestVideo.LastKnownLocationMarkerLabel"), subIcon: "videocam", showLabel: true });
    }

    private buildPolylineFromExtensionData(): angular.IPromise<void> {
        this.clearMapMarkers();
        this.videoMarker = null;

        this.createVideoMarker();
        const extensionData = this.video.VideoExtensionData;

        const plots = extensionData.VideoGpsData.map(data => { return { Latitude: data.Latitude, Longitude: data.Longitude } })

        this.createStartMarker(plots[0]);
        this.createEndMarker(plots[plots.length - 1]);


        return this.cameraDisplayService.createPolylinesFromPlots(plots).then(polyline => {
            this.vehiclePolyline = polyline;
        });
    }


    private renderMap(): angular.IPromise<void> {
        this.loading = true;
        this.vehiclePolyline = null;

        this.clearMapMarkers();

        const requestTime = this.video ? this.video.EventTimeUtc : this.requestVideoTime;

        if (this.dateService.date1IsAfterDate2(requestTime, new Date())) {
            this.loading = false;
            return;
        }

        const showLastKnownLocation: boolean = !this.video && this.isLiveStreamCamera && !this.isOnline;

        const plotsPromise = (showLastKnownLocation)
            ? this.plotService.getLatestPlotForVehicle(this.vehicle.Id)
            : this.getPlots();

        return plotsPromise.then(() => {
            this.buildJourneyMap(showLastKnownLocation);
        })
            .catch(err => {
                console.warn(err)
            })
            .finally(() => {
                this.loading = false;
            });
    }

    private requestTimeHasChanged(change: angular.IChangesObject<Date>): boolean {
        if (!change.previousValue) {
            return true;
        }

        const currentTime = new Date(change.previousValue);
        const newTime = new Date(change.currentValue);

        return currentTime.getTime() !== newTime.getTime();

    }
}

export class RequestVideoMapComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            vehicle: "<",
            video: "<",
            requestVideoTime: "<",
            videoLength: "<",
            isLiveStreamCamera: "<",
            isOnline: "<",
            onClosestPlotFound: "&",
            onJourneysLoaded: "&"
        };

        this.controller = ComponentController;
        this.template = require("./requestVideoMap.component.html");
    }
}
